import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiLoader, FiMail } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useFormValidation } from '../../hooks/useFormValidation';
import { validateRequired, validateEmail, validateMinLength } from '../../utils/validation';
import { useLanguage } from '../../i18n';
import './ContactForm.css';

const ContactForm = () => {
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting] = useState(false);
  const { t, language } = useLanguage();

  const initialValues = {
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  };

  // Email passa a ser opcional; valida apenas se preenchido
  const validationRules = {
    name: [validateRequired, validateMinLength(2)],
    email: [validateEmail],
    subject: [validateRequired, validateMinLength(5)],
    message: [validateRequired, validateMinLength(10)]
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit, // mantido caso necessário futuramente
    resetForm,
    validateForm
  } = useFormValidation(initialValues, validationRules);

  const buildMessage = () => {
    const parts = [
      `Nome: ${values.name}`,
      values.email ? `Email: ${values.email}` : null,
      values.phone ? `Telefone: ${values.phone}` : null,
      `Assunto: ${values.subject}`,
      '',
      values.message
    ].filter(Boolean);
    return parts.join('\n');
  };

  const handleSendWhatsApp = () => {
    // valida somente os campos obrigatórios usando validateForm
    const ok = validateForm();
    if (!ok) {
      setSubmitStatus({ type: 'error', message: 'Preencha os campos obrigatórios antes de enviar.' });
      return;
    }
    const phone = '5575998042767';
    const text = encodeURIComponent(buildMessage());
    const url = `https://wa.me/${phone}?text=${text}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSendEmail = () => {
    const ok = validateForm();
    if (!ok) {
      setSubmitStatus({ type: 'error', message: 'Preencha os campos obrigatórios antes de enviar.' });
      return;
    }
    const to = 'pedroworkdev@gmail.com';
    const subject = encodeURIComponent(values.subject || 'Contato do Portfólio');
    const body = encodeURIComponent(buildMessage());
    const mailto = `mailto:${to}?subject=${subject}&body=${body}`;
    window.location.href = mailto;
  };

  return (
    <motion.div
      className="contact-form"
      initial={{ opacity: 0, x: 30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <div className="contact-form__header">
        <h3>{language === 'en' ? 'Send a message' : language === 'es' ? 'Envía un mensaje' : 'Envie uma mensagem'}</h3>
        <p>{language === 'en' ? 'Fill in Name, Subject and Message, then choose WhatsApp or Email.' : language === 'es' ? 'Complete Nombre, Asunto y Mensaje, y elija WhatsApp o Email.' : 'Preencha Nome, Assunto e Mensagem, e escolha enviar por WhatsApp ou Email.'}</p>
      </div>

      {submitStatus && (
        <motion.div
          className={`contact-form__message contact-form__message--${submitStatus.type}`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
        >
          {submitStatus.message}
        </motion.div>
      )}

      <form noValidate>
        <div className="contact-form__row">
          <div className="contact-form__group">
            <label htmlFor="name" className="contact-form__label">
              {t('contact.name')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`contact-form__input ${errors.name && touched.name ? 'contact-form__input--error' : ''}`}
              placeholder="Seu nome completo"
              disabled={isSubmitting}
            />
            {errors.name && touched.name && (
              <span className="contact-form__error">{errors.name}</span>
            )}
          </div>

          <div className="contact-form__group">
            <label htmlFor="email" className="contact-form__label">
              {t('contact.email')} ({language === 'en' ? 'optional' : language === 'es' ? 'opcional' : 'opcional'})
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`contact-form__input ${errors.email && touched.email ? 'contact-form__input--error' : ''}`}
              placeholder="seu.email@exemplo.com"
              disabled={isSubmitting}
            />
            {errors.email && touched.email && (
              <span className="contact-form__error">{errors.email}</span>
            )}
          </div>
        </div>

        <div className="contact-form__row">
          <div className="contact-form__group">
            <label htmlFor="phone" className="contact-form__label">
              Telefone (opcional)
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={values.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              className="contact-form__input"
              placeholder="(00) 00000-0000"
              disabled={isSubmitting}
            />
          </div>

          <div className="contact-form__group">
            <label htmlFor="subject" className="contact-form__label">
              {t('contact.subject')} *
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={values.subject}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`contact-form__input ${errors.subject && touched.subject ? 'contact-form__input--error' : ''}`}
              placeholder="Assunto da mensagem"
              disabled={isSubmitting}
            />
            {errors.subject && touched.subject && (
              <span className="contact-form__error">{errors.subject}</span>
            )}
          </div>
        </div>

        <div className="contact-form__group">
          <label htmlFor="message" className="contact-form__label">
            {t('contact.message')} *
          </label>
          <textarea
            id="message"
            name="message"
            value={values.message}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`contact-form__textarea ${errors.message && touched.message ? 'contact-form__textarea--error' : ''}`}
            placeholder="Descreva seu projeto ou como posso ajudá-lo..."
            rows="5"
            disabled={isSubmitting}
          />
          {errors.message && touched.message && (
            <span className="contact-form__error">{errors.message}</span>
          )}
        </div>

        <div className="contact-form__actions">
          <motion.button
            type="button"
            className="contact-form__button contact-form__button--whats"
            onClick={handleSendWhatsApp}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="contact-form__button-icon"><FaWhatsapp /></span>
            {t('contact.sendWhats')}
          </motion.button>

          <motion.button
            type="button"
            className="contact-form__button contact-form__button--email"
            onClick={handleSendEmail}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="contact-form__button-icon"><FiMail /></span>
            {t('contact.sendEmail')}
          </motion.button>
        </div>

        <p className="contact-form__note">
          * Campos obrigatórios. Seus dados estão protegidos e não serão compartilhados.
        </p>
      </form>
    </motion.div>
  );
};

export default ContactForm;